import { useEffect, useState } from 'react';
import { callAction } from '../../app/actions';
import type { AppTarget } from '../targets/TargetsScreen';
import {
  CONTEXT_SOURCES,
  ctxTriParse,
  ctxTriValue,
  inheritedAppTarget,
  inheritsAs,
  type ContextOverrides,
  type GroundingDefaults,
} from '../../lib/aiFields';
import type { Edges, MapNode, NodeMap } from '../../lib/types';
import { useAppState } from '../../app/state';
import { CountPill, PopupFold } from './PopupFold';
import { SECTION_AI, SECTION_AI_GROUNDING, SECTION_AI_LIVE } from './sections';

/**
 * The node popup's AI-documentation panel: the prompt, a Generate button, the four grounding
 * overrides, and the live system the agent may inspect.
 *
 * THE STATUS SELECT DIED WITH V0.38: a node's pipeline stage is DERIVED from its primary
 * page's workflow now, never hand-set — the doc bar's WorkflowControl is where a state moves,
 * because that is where the text being judged is readable. What replaced the select is the
 * Generate button below the prompt: the responsible's relaunch loop, guarded server-side
 * against overwriting human edits (409 → confirm → force).
 *
 * WHERE THE DOM READ-BACK WENT. The vanilla panel was written as an HTML string and read back
 * out of the elements it had written (`aiFieldsRead`), which is the shape CLAUDE.md names as
 * the most likely source of silent data loss in the whole migration — between the render and
 * the save, the values existed nowhere else, so anything that replaced or hid an input
 * discarded what was in it with nothing to notice. Every field here is state, held by
 * `PopupBody` because the Save button needs it too, and `applyAiFields()` on the vanilla side
 * takes the values rather than fetching them.
 *
 * THE PROBE TOGGLE NOW DRIVES THE WHOLE BLOCK (V0.45). It used to update only its own button
 * and the visibility of the detail below it — the note and the "Run live verification" button
 * came from the node's STORED `liveProbeEnabled`, because the vanilla toggle was four
 * imperative DOM writes that re-computed neither, and the port reproduced that faithfully.
 * The consequence, watched in the self-documentation walkthrough: turning the probe on
 * revealed the target picker and no way to use it, and the run appeared only after Save and a
 * re-open — with nothing on screen saying so. `stored` is gone; `on` and the effective target
 * both read the DRAFT.
 *
 * The button still requires an EFFECTIVE target — one chosen in the draft, or inherited from
 * an ancestor — so probe-on with nothing to point at offers no dead end, which is also what
 * keeps `e2e/36-popup.spec.ts` green on the case it pins.
 *
 * ONE THING THAT LOOKS LIKE A BUG AND IS PORTED ANYWAY, because "identical behaviour" is the
 * mandate and it is observable:
 *
 *  - The live-system block is NOT gated on the `liveVerify` capability, only on the AI
 *    operator's panel being drawn at all (AI-doc mode until V0.50; the AI_ADMIN right
 *    since). `startLiveVerify()` is gated, so an offline build cannot start a run from here —
 *    but the controls are drawn. That is what the incumbent does; widening or narrowing it is
 *    a change, and a change is out of scope.
 */

export interface AiDraft {
  aiPrompt: string;
  ctx: ContextOverrides;
  liveProbeEnabled: boolean;
  /** `''` is "none / inherit", matching the empty `<option>`'s value. */
  appTargetId: string;
  appPath: string;
  /**
   * Whether this panel DRAWS the generation mechanics (V0.37). True for the AI operator's
   * panel (AI-doc mode); false for a responsible's prompt-only panel, whose node payload
   * never carried the mechanics fields at all — `applyAiFields` and the flush both honour it,
   * so a prompt edit can never smuggle a mechanics write.
   */
  mechanics: boolean;
}

export function aiDraftOf(n: MapNode, mechanics: boolean): AiDraft {
  const ctx = {} as ContextOverrides;
  for (const s of CONTEXT_SOURCES) ctx[s.key] = (n[s.key] ?? null) as boolean | null;
  return {
    aiPrompt: n.aiPrompt || '',
    ctx,
    liveProbeEnabled: !!n.liveProbeEnabled,
    appTargetId: n.appTargetId || '',
    appPath: n.appPath || '',
    mechanics,
  };
}

/* THE OPEN STATE IS `PopupFold`'s NOW (V0.51), with every other section of the popup: shut by
   default, and remembered PER USER and per work day rather than per tab. It used to live in
   `sessionStorage` under `AIPANEL_STORE`, which made this the one fold in the form with a
   lifetime of its own — a tab's, not a person's. That key is gone from `store/persistence.ts`.

   The default has been CLOSED since V0.50 (it shipped in that release without a note; the V0.51
   entry records it late), which is the other half of taking the panel off AI-doc mode
   (`aiModeAtOpen` in NodePopup.tsx): while the mode was the gate, "open" was right, because the
   panel was only ever drawn for somebody who had just asked for the AI tools. It is drawn on
   every node popup an operator opens now, so it costs one summary line until it is wanted. */

export interface AiPanelProps {
  node: MapNode;
  nodes: NodeMap;
  edges: Edges;
  cfg: GroundingDefaults | null;
  targets: readonly AppTarget[];
  targetsLoaded: boolean;
  draft: AiDraft;
  onChange: (next: AiDraft) => void;
  /**
   * Whether this deployment can generate at all (`capAi('docgen')`) — the offline build must
   * show the Generate button as ABSENT rather than present-and-503ing. The panel itself is
   * already scoped to `canEditPrompt` territory by NodePopup's `aiModeAtOpen`.
   */
  canGenerate: boolean;
}

/** "all inherit", or how many of the four this node overrides — the heading's whole job. */
function groundingLabel(draft: AiDraft): string {
  const set = CONTEXT_SOURCES.filter((s) => draft.ctx[s.key] !== null).length;
  return set ? `${set} of ${CONTEXT_SOURCES.length} overridden` : 'all inherit';
}

/** "off", or the target the agent may inspect — read from the DRAFT, like the block itself. */
function liveLabel(draft: AiDraft, targets: readonly AppTarget[]): string {
  if (!draft.liveProbeEnabled) return 'off';
  const t = targets.find((x) => x.id === draft.appTargetId);
  return t ? `on · ${t.name}` : 'on';
}

export function AiPanel({
  node,
  nodes,
  edges,
  cfg,
  targets,
  targetsLoaded,
  draft,
  onChange,
  canGenerate,
}: AiPanelProps) {
  const open = useAppState().openSections.has(SECTION_AI);
  const [generating, setGenerating] = useState(false);
  const set = <K extends keyof AiDraft>(k: K, v: AiDraft[K]) => onChange({ ...draft, [k]: v });

  /**
   * The two caches the mechanics read, fetched the first time this panel is actually OPEN.
   *
   * `setAiDocMode(true)` used to fetch them on mode entry, which was enough while the mode was
   * also what drew the panel. V0.50 took the panel off the mode, so an operator reaches
   * these fields without ever entering it — the grounding config is what labels each
   * "Inherit (on/off)", and `targetsLoaded` is what tells "no targets configured" apart from
   * "we have not asked yet".
   *
   * ON OPEN, NOT ON MOUNT: the panel is now drawn on every popup an operator opens, and two
   * requests per popup for a section nobody expanded is exactly the cost this being collapsed
   * exists to avoid. Both loaders no-op when the value is already held, and a failure leaves
   * the dependency unchanged so this does not become a retry loop.
   */
  useEffect(() => {
    if (!open || !draft.mechanics) return;
    if (!cfg) void callAction('loadAiDocCfgCache');
    if (!targetsLoaded) void callAction('loadAppTargets');
  }, [open, draft.mechanics, cfg, targetsLoaded]);

  return (
    <PopupFold sectionKey={SECTION_AI} id="pp-aipanel" label="AI documentation">
      <>
        <label>AI prompt</label>
        <textarea
          id="pp-aiprompt"
          placeholder="Instructions for the AI agent writing this node's documentation…"
          value={draft.aiPrompt}
          onChange={(e) => set('aiPrompt', e.target.value)}
        />

        {/* The relaunch loop (V0.38): pending prompt edits are written first, then the one
            409/confirm/force flow in actions.ts runs — the same helper the WorkflowControl's
            Regenerate uses, so the guard can never grow two wordings. */}
        {canGenerate ? (
          <button
            type="button"
            className="pbtn primary"
            id="pp-generate"
            style={{ marginTop: 9, fontSize: 12.5, padding: '5px 11px', alignSelf: 'flex-start' }}
            disabled={generating || !draft.aiPrompt.trim()}
            title={
              draft.aiPrompt.trim()
                ? 'Write this node’s documentation with the AI. Human edits since the last generation are guarded.'
                : 'Write a prompt first.'
            }
            onClick={() => {
              setGenerating(true);
              void (callAction('generateDocFromPopup', node.id, draft) as Promise<boolean>).finally(
                () => setGenerating(false),
              );
            }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        ) : null}

        {/* The MECHANICS half — context sources and the live system — is AI_ADMIN's and is
            simply absent from a responsible's prompt-only panel (V0.37): the payload never
            carried these fields for them, and drawing defaults would be drawing fiction. */}
        {/* TWO NESTED FOLDS (V0.51). Expanded, the panel was a prompt, a button, four
            tri-state selects and the whole live-system block — a console, when what somebody
            opens it for nine times in ten is the prompt. The mechanics keep their own two
            levels, each stating its answer in its heading, so "all inherit" and "off" are
            readable without opening either. */}
        {draft.mechanics ? (
          <>
            <PopupFold
              sectionKey={SECTION_AI_GROUNDING}
              className="sub"
              label="Grounding overrides"
              brief={<CountPill>{groundingLabel(draft)}</CountPill>}
              summary={{ pills: [<CountPill key="g">{groundingLabel(draft)}</CountPill>], plusClass: 'count-pill' }}
            >
              <>
            {CONTEXT_SOURCES.map((s) => (
              <label className="filterrow ctxrow" key={s.key}>
                <span>{s.label}</span>
                <select
                  className="ctx-tri"
                  data-ctxkey={s.key}
                  value={ctxTriValue(draft.ctx[s.key])}
                  onChange={(e) =>
                    onChange({ ...draft, ctx: { ...draft.ctx, [s.key]: ctxTriParse(e.target.value) } })
                  }
                >
                  {/* The inherit option says what inheriting resolves to right now, so an admin
                      can see the effective answer without opening the Context screen. */}
                  <option value="">Inherit ({inheritsAs(cfg, s.key)})</option>
                  <option value="true">On</option>
                  <option value="false">Off</option>
                </select>
              </label>
            ))}
              </>
            </PopupFold>

            <PopupFold
              sectionKey={SECTION_AI_LIVE}
              className="sub"
              label="Live system"
              brief={<CountPill>{liveLabel(draft, targets)}</CountPill>}
              summary={{ pills: [<CountPill key="l">{liveLabel(draft, targets)}</CountPill>], plusClass: 'count-pill' }}
            >
              <LiveSystem
                node={node}
                nodes={nodes}
                edges={edges}
                targets={targets}
                targetsLoaded={targetsLoaded}
                draft={draft}
                onChange={onChange}
              />
            </PopupFold>
          </>
        ) : null}
      </>
    </PopupFold>
  );
}

function LiveSystem({
  node,
  nodes,
  edges,
  targets,
  targetsLoaded,
  draft,
  onChange,
}: Omit<AiPanelProps, 'cfg' | 'canGenerate'>) {
  // Everything here reads the DRAFT, so the block answers the toggle the moment it moves.
  const byId = (id: string | null | undefined): AppTarget | null =>
    targets.find((t) => t.id === id) || null;

  const on = draft.liveProbeEnabled;
  const inherited = inheritedAppTarget(node.id, nodes, edges);
  // The EFFECTIVE target: what this node would actually be probed against — the draft's own
  // choice, or the nearest ancestor's, which is the same nearest-ancestor-wins resolution the
  // server performs.
  const eff = draft.appTargetId ? byId(draft.appTargetId) : inherited ? byId(inherited.targetId) : null;

  let note = '';
  if (!targetsLoaded) note = 'Loading targets…';
  else if (!targets.length) note = 'No targets configured yet — add one in the Targets screen.';
  else if (on && !eff) note = 'Pick a target, or set one on a parent node to inherit it.';
  else if (on && eff && !eff.liveTestingEnabled)
    note = `“${eff.name}” is disarmed, so no live run can start. Arm it in the Targets screen.`;
  else if (on && eff) note = `Ready — the agent may inspect “${eff.name}” for this node.`;

  const armed = !!(eff && eff.liveTestingEnabled);
  // The run writes the draft to the server before it starts, so the button says so rather
  // than looking inert for the length of a PUT.
  const [starting, setStarting] = useState(false);

  return (
    <div className="liveblk">
      <label className="filterrow ctxrow" style={{ paddingLeft: 0 }}>
        <span>Probe the live system with the agent</span>
        <button
          type="button"
          className={draft.liveProbeEnabled ? 'pbtn probe-toggle on' : 'pbtn probe-toggle'}
          id="pp-probe"
          aria-pressed={draft.liveProbeEnabled}
          onClick={() => onChange({ ...draft, liveProbeEnabled: !draft.liveProbeEnabled })}
        >
          {draft.liveProbeEnabled ? 'On' : 'Off'}
        </button>
      </label>
      <div id="pp-live-detail" style={draft.liveProbeEnabled ? undefined : { display: 'none' }}>
        <div className="tgt-conn" style={{ marginTop: 8 }}>
          <select
            id="pp-apptarget"
            className="fld"
            value={draft.appTargetId}
            onChange={(e) => onChange({ ...draft, appTargetId: e.target.value })}
          >
            <option value="">
              {inherited
                ? `Inherit — ${byId(inherited.targetId)?.name ?? 'unknown'}`
                : '— None —'}
            </option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.liveTestingEnabled ? '' : ' (disarmed)'}
              </option>
            ))}
          </select>
          <input
            id="pp-apppath"
            className="fld"
            value={draft.appPath}
            onChange={(e) => onChange({ ...draft, appPath: e.target.value })}
            // Ported as written. The condition reduces to "a database target wants a table
            // name, everything else wants a path", but it is left in the shape the original
            // had it rather than tidied — a placeholder that changed would be a change.
            placeholder={
              node.appPath || eff
                ? eff && eff.kind === 'database'
                  ? 'table name (optional)'
                  : '/path/in/the/app'
                : '/path/in/the/app'
            }
          />
        </div>
        {note ? (
          <div className="tgt-meta" style={{ marginTop: 6 }}>
            {note}
          </div>
        ) : null}
        {node.liveObservationAt ? (
          <div className="tgt-meta" style={{ marginTop: 6 }}>
            Live-verified {new Date(node.liveObservationAt).toLocaleDateString()}
          </div>
        ) : null}
        {/* Drawn once the probe is on AND there is something to point at — a target chosen
            here or inherited from an ancestor. Enabled only when that target is armed, which
            is the same AND rule the server enforces, so the button is never a dead end. */}
        {on && eff ? (
          <button
            type="button"
            className={armed ? 'pbtn primary' : 'pbtn'}
            id="pp-runverify"
            style={{ marginTop: 9, fontSize: 12.5, padding: '5px 11px' }}
            disabled={!armed || starting}
            title={armed ? undefined : 'Set an armed target first'}
            onClick={() => {
              // The server resolves the target from the node's STORED state, so the draft is
              // written and AWAITED first — otherwise an unsaved target is silently ignored
              // and the run visits whatever the node used to point at.
              setStarting(true);
              void Promise.resolve(callAction('runLiveVerifyFromPopup', node.id, draft)).finally(() =>
                setStarting(false),
              );
            }}
          >
            {starting ? 'Starting…' : 'Run live verification'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
